import React, { useState } from "react";
import { T } from "../lib/theme";
import { getDealers, fetchDealersViaClyde, fetchRecalls } from "../lib/dealersAndRecalls";
import { requestFullAccess } from "../lib/utils";
import { ModeBadge, Spin } from "./Atoms";

export function DealerFinder({vehicle,mode}){
  const[state,setState]=useState("idle"); // idle|requesting|loading|done|error
  const[dealers,setDealers]=useState([]);
  const[error,setError]=useState("");
  const[open,setOpen]=useState(false);
  const[demoBlocked,setDemoBlocked]=useState(false); // true only when the server refused live search for this account's tier — distinct from mode==="demo", which never even attempts the real call
  const[goLiveSent,setGoLiveSent]=useState(false);
  const demoDealers=getDealers(vehicle);

  const findNearMe=()=>{
    if(mode==="demo"){setState("done");setDealers(demoDealers.map(d=>({...d,isMake:true})));setOpen(true);return;}
    setState("requesting");setDemoBlocked(false);
    navigator.geolocation.getCurrentPosition(
      async pos=>{
        setState("loading");setError("");
        try{
          const results=await fetchDealersViaClyde(vehicle.make,pos.coords.latitude,pos.coords.longitude);
          setDealers(results);setState("done");setOpen(true);
        }catch(e){
          // A demo-tier account's local mode toggle can claim "live"
          // independently of its real access_level — the server is the
          // source of truth and refuses accordingly. Same graceful
          // fallback as the mode==="demo" branch above, not an error:
          // the account genuinely doesn't get live dealer search, but
          // it should still see a normal-looking result, not a failure.
          // demoBlocked (distinct from the mode==="demo" case above)
          // drives a visible note explaining why — the fallback data is
          // the same, but silently substituting it with no explanation
          // would look like a bug rather than a real plan limit.
          if(e.reason==="demo_blocked"){
            setDemoBlocked(true);setState("done");setDealers(demoDealers.map(d=>({...d,isMake:true})));setOpen(true);
          }else{
            setState("error");setError(e.message||"Could not load dealers.");
          }
        }
      },
      err=>{
        setState("error");
        setError(err.code===1?"Location access denied — allow location and try again.":"Could not get your location.");
      },
      {timeout:10000,maximumAge:300000}
    );
  };

  const reset=()=>{setState("idle");setDealers([]);setOpen(false);setError("");setDemoBlocked(false);};

  return(
    <div style={{borderRadius:12,border:`1.5px solid ${T.border}`,background:T.card,overflow:"hidden"}}>
      <button onClick={state==="done"?()=>setOpen(o=>!o):findNearMe} disabled={state==="requesting"||state==="loading"} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:"none",border:"none",textAlign:"left",cursor:"pointer"}}>
        <span style={{fontSize:18}}>📍</span>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>Nearby {vehicle.make} Dealers</div>
            <ModeBadge mode={mode}/>
          </div>
          <div style={{fontSize:12,color:T.textSm,marginTop:1}}>
            {state==="idle"&&(mode==="live"?"Use your location to find real dealers nearby":"Tap to see nearby dealers")}
            {state==="requesting"&&"Requesting location..."}
            {state==="loading"&&"Researching nearby dealers..."}
            {state==="done"&&`${dealers.length} dealer${dealers.length!==1?"s":""} found${mode==="live"?" via Clyde":""}`}
            {state==="error"&&<span style={{color:T.red}}>{error}</span>}
          </div>
        </div>
        {(state==="requesting"||state==="loading")?<Spin/>:<span style={{color:T.textSm,fontSize:16,transform:open?"rotate(180deg)":"rotate(0deg)",transition:"transform .25s"}}>v</span>}
      </button>

      {state==="done"&&open&&(
        <div style={{borderTop:`1px solid ${T.border}`}}>
          {demoBlocked&&<div style={{padding:"9px 16px",fontSize:12,color:T.textMd,background:T.amberL,borderBottom:`1px solid ${T.border}`,lineHeight:1.5}}>Real dealer search unlocks once your account is reviewed. Here's a sample list for now. {goLiveSent?<span style={{color:T.teal,fontWeight:700}}>Request sent ✓</span>:<button onClick={()=>requestFullAccess().then(setGoLiveSent)} style={{background:"none",border:"none",padding:0,color:T.teal,fontWeight:700,textDecoration:"none",cursor:"pointer",font:"inherit"}}>Request faster review</button>} for the real thing.</div>}
          {dealers.length===0?(
            <div style={{padding:"14px 16px",fontSize:13,color:T.textSm}}>No dealers found nearby. Try again, or search manually.</div>
          ):(
            <>
              <div>
                <div style={{padding:"6px 16px",background:T.tealL,borderBottom:`1px solid ${T.border}`,fontSize:11,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:".06em"}}>{vehicle.make} Dealers</div>
                {dealers.map((d,i)=><DealerCard key={d.id} dealer={d} isLast={i===dealers.length-1}/>)}
              </div>
              <div style={{padding:"10px 16px",background:T.slateL,borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontSize:11,color:T.textSm}}>{mode==="live"?"Researched via Clyde — verify before visiting":"Demo data"}</div>
                {mode==="live"&&<button onClick={reset} style={{fontSize:11,color:T.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600}}>Refresh</button>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DealerCard({dealer,isLast}){
  return(
    <div style={{padding:"12px 16px",borderBottom:isLast?"none":`1px solid ${T.border}`}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:2}}>{dealer.name}</div>
      <div style={{fontSize:12,color:T.textSm,marginBottom:6}}>{dealer.addr}</div>
      {(dealer.dist||dealer.rating||dealer.wait)&&<div style={{display:"flex",gap:10,marginBottom:(dealer.phone||dealer.website||dealer.email)?8:0,flexWrap:"wrap"}}>
        {dealer.dist&&<span style={{fontSize:11,color:T.textSm}}>📍 {dealer.dist}</span>}
        {dealer.rating&&<span style={{fontSize:11,color:T.amber,fontWeight:600}}>★ {dealer.rating}</span>}
        {dealer.wait&&<span style={{fontSize:11,color:T.green,fontWeight:600}}>{dealer.wait}</span>}
      </div>}
      {(dealer.phone||dealer.website||dealer.email)&&(
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {dealer.phone&&(
            <a href={`tel:${dealer.phone}`} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",background:T.slateL,color:T.text,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12,fontWeight:600,textDecoration:"none"}}>
              📞 {dealer.phone}
            </a>
          )}
          {dealer.website&&(
            <a href={dealer.website} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",background:T.slateL,color:T.text,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12,fontWeight:600,textDecoration:"none"}}>
              🌐 Website
            </a>
          )}
          {dealer.email&&(
            <a href={`mailto:${dealer.email}`} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",background:T.slateL,color:T.text,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12,fontWeight:600,textDecoration:"none"}}>
              ✉️ Email
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function RecallAndService({vehicle,mode}){
  const[dealersOpen,setDealersOpen]=useState(false);const[loading,setLoading]=useState(false);const[checked,setChecked]=useState(false);const[recalls,setRecalls]=useState([]);const[recallSource,setRecallSource]=useState(null);const[recallError,setRecallError]=useState("");
  const[recallsExpanded,setRecallsExpanded]=useState(false);
  const dealers=getDealers(vehicle);
  const checkRecalls=async()=>{setLoading(true);setRecallError("");try{const{items,source}=await fetchRecalls(vehicle);setRecalls(items);setRecallSource(source);setChecked(true);}catch(e){setRecallError(e.message||"Could not load recall data.");}setLoading(false);};
  const openRecalls=recalls.filter(r=>r.status==="open");const fixedRecalls=recalls.filter(r=>r.status==="remedy_available");
  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{borderRadius:12,border:`1.5px solid ${checked&&openRecalls.length>0?"#fca5a5":T.border}`,background:checked&&openRecalls.length>0?T.redL:T.card,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:checked||recallError?`1px solid ${T.border}`:"none"}}>
          <span style={{fontSize:18}}>🚨</span>
          <div style={{flex:1}}><div style={{display:"flex",alignItems:"center"}}><div style={{fontSize:13,fontWeight:700,color:T.text}}>NHTSA Recall Check</div>{checked&&<ModeBadge mode={recallSource}/>}</div><div style={{fontSize:12,color:T.textSm,marginTop:2}}>{!checked&&!recallError&&(vehicle.vin?`VIN: ${vehicle.vin}`:"Add VIN for precise results")}{checked&&openRecalls.length===0&&fixedRecalls.length===0&&"No open recalls found"}{checked&&(openRecalls.length>0||fixedRecalls.length>0)&&`${openRecalls.length} open — ${fixedRecalls.length} remedy available`}{recallError&&<span style={{color:T.red}}>{recallError}</span>}</div></div>
          {!checked&&!recallError&&<button onClick={checkRecalls} disabled={loading} style={{background:`linear-gradient(135deg,${T.red},#b91c1c)`,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6,flexShrink:0}}>{loading?<><Spin sz={13} stroke="#fff" track="rgba(255,255,255,.3)"/>Checking...</>:"Check Now"}</button>}
          {checked&&recalls.length>0&&<button onClick={()=>setRecallsExpanded(v=>!v)} style={{fontSize:11,color:T.text,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,cursor:"pointer",padding:"5px 10px",flexShrink:0,fontWeight:600}}>{recallsExpanded?"Hide ▴":"View ▾"}</button>}
          {(checked||recallError)&&<button onClick={()=>{setChecked(false);setRecalls([]);setRecallError("");setRecallsExpanded(false);}} style={{fontSize:11,color:T.textSm,background:"none",border:"none",cursor:"pointer",padding:"4px 8px",flexShrink:0}}>Recheck</button>}
        </div>
        {checked&&recallsExpanded&&recalls.length>0&&recalls.map((r,i)=>(<div key={r.id} style={{padding:"12px 16px",borderBottom:i<recalls.length-1?`1px solid ${T.border}`:"none"}}><div style={{display:"flex",gap:10,marginBottom:6}}><span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,background:r.status==="open"?T.redL:T.amberL,color:r.status==="open"?T.red:T.amber,textTransform:"uppercase",flexShrink:0}}>{r.status==="open"?"Open":"Remedy Available"}</span><span className="mono" style={{fontSize:11,color:T.textSm}}>{r.campaign}</span></div><div style={{fontSize:12,fontWeight:600,color:T.text,marginBottom:4}}>{r.component}</div><div style={{fontSize:12,color:T.textMd,lineHeight:1.6,marginBottom:4}}>{r.summary}</div><div style={{fontSize:12,color:T.green,fontWeight:600}}>Fix: {r.remedy}</div></div>))}
        {checked&&recalls.length===0&&<div style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}><span>✅</span><span style={{fontSize:13,color:T.green,fontWeight:600}}>No open recalls on record.</span></div>}
      </div>
      <DealerFinder vehicle={vehicle} mode={mode}/>
    </div>
  );
}
